import React, { useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetStoreTransactionsQuery,
  useGetSkusQuery,
  useGetStockEditRequestsQuery,
  appApi,
} from "../../../../store/appApi";
import {
  selectUserRole,
  selectHasModuleAccess,
} from "../../../../store/authSlice";
import {
  updateStoreEntryApi,
  createEditRequestApi,
} from "../../../../services/inventory";
import PageTitle from "../../../layouts/PageTitle";
import ExcelDownloadButton from "../../../components/ExcelDownloadButton";
import { StockBulkUploadModal } from "./BulkUploadModals";
import "./style.css";
const ADMIN_ROLES = ["super-admin", "sub-admin"];

const PAGE_SIZE = 100;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";
const fmtNum = (n) => (n != null ? Number(n).toLocaleString("en-IN") : "—");

const EDIT_EMPTY = { qty: "", price: "", invoiceNo: "" };
const REQUEST_EMPTY = { newQty: "", newPrice: "", reason: "" };

const List = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userRole = useSelector(selectUserRole);
  const isAdmin = ADMIN_ROLES.includes(userRole);
  const hasInventoryWrite = useSelector(
    selectHasModuleAccess("inventory", "read-write"),
  );

  const [page, setPage] = useState(1);
  const [skuFilter, setSkuFilter] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const isSuperAdmin = userRole === "super-admin";
  const [typeFilter, setTypeFilter] = useState("");

  const { data: skuData } = useGetSkusQuery();
  const skus = Array.isArray(skuData?.skus) ? skuData.skus : [];

  const {
    data: txnData,
    isLoading,
    isFetching,
  } = useGetStoreTransactionsQuery({
    skuId: skuFilter,
    entryType: typeFilter,
    page,
    limit: PAGE_SIZE,
  });
  const transactions = Array.isArray(txnData?.stocks) ? txnData.stocks : [];
  const totalCount = txnData?.total ?? transactions.length;
  const totalPages = txnData?.totalPages ?? 1;
  const loading = isLoading || isFetching;

  // Fetch pending edit requests so we can show a badge per row
  const { data: erData } = useGetStockEditRequestsQuery("pending");
  const pendingRequests = Array.isArray(erData?.requests)
    ? erData.requests
    : [];
  const pendingByStock = {};
  pendingRequests.forEach((r) => {
    const sid = r.stockId?._id || r.stockId;
    if (sid) pendingByStock[sid.toString()] = r;
  });

  // ── Super-admin direct edit modal ───────────────────────────────────────────
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(EDIT_EMPTY);
  const [editLoading, setEditLoading] = useState(false);

  const openEdit = (t) => {
    const req = pendingByStock[t._id?.toString()];
    setEditRow({ ...t, _pendingReq: req || null });
    setEditForm({
      qty: req ? req.newQty : (t.qty ?? ""),
      price: req ? (req.newPrice ?? t.price ?? "") : (t.price ?? ""),
      invoiceNo: t.invoiceNo ?? "",
    });
  };
  const closeEdit = () => {
    setEditRow(null);
    setEditForm(EDIT_EMPTY);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.qty || Number(editForm.qty) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid qty",
        text: "Enter a valid quantity.",
      });
      return;
    }
    try {
      setEditLoading(true);
      await updateStoreEntryApi({
        _id: editRow._id,
        qty: Number(editForm.qty),
        price: editForm.price !== "" ? Number(editForm.price) : undefined,
      });
      dispatch(
        appApi.util.invalidateTags(["StoreTransactions", "StockEditRequests"]),
      );
      closeEdit();
      Swal.fire({
        icon: "success",
        title: "Updated",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Update failed.",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // ── Employee request-edit modal ─────────────────────────────────────────────
  const [requestRow, setRequestRow] = useState(null);
  const [requestForm, setRequestForm] = useState(REQUEST_EMPTY);
  const [requestLoading, setRequestLoading] = useState(false);

  const openRequest = (t) => {
    setRequestRow(t);
    setRequestForm({
      newQty: t.qty ?? "",
      newPrice: t.price ?? "",
      reason: "",
    });
  };
  const closeRequest = () => {
    setRequestRow(null);
    setRequestForm(REQUEST_EMPTY);
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!requestForm.newQty || Number(requestForm.newQty) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid qty",
        text: "Enter a valid quantity.",
      });
      return;
    }
    if (!requestForm.reason.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Reason required",
        text: "Please explain why you need this edit.",
      });
      return;
    }
    try {
      setRequestLoading(true);
      await createEditRequestApi({
        stockId: requestRow._id,
        newQty: Number(requestForm.newQty),
        newPrice:
          requestForm.newPrice !== ""
            ? Number(requestForm.newPrice)
            : undefined,
        reason: requestForm.reason.trim(),
      });
      dispatch(appApi.util.invalidateTags(["StockEditRequests"]));
      closeRequest();
      Swal.fire({
        icon: "success",
        title: "Request Submitted",
        text: "Admin will review your edit request.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.response?.data?.message || "Could not submit request.",
      });
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Store Transactions" motherMenu="Inventory" />
      {isSuperAdmin && (
        <StockBulkUploadModal
          show={bulkOpen}
          onHide={() => setBulkOpen(false)}
          onDone={() => dispatch(appApi.util.invalidateTags(["StoreTransactions"]))}
        />
      )}
      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label mb-1 small fw-semibold">
                Filter by Item
              </label>
              <select
                className="form-select form-select-sm"
                value={skuFilter}
                onChange={(e) => {
                  setSkuFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Items</option>
                {skus.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.skuName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label mb-1 small fw-semibold">
                Filter by Type
              </label>
              <select
                className="form-select form-select-sm"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Types</option>
                <option value="add">Added</option>
                <option value="issued">Issued</option>
              </select>
            </div>
            <div className="col-md-auto">
              <button
                className="btn btn-sm btn-outline-secondary align-center"
                onClick={() => {
                  setSkuFilter("");
                  setTypeFilter("");
                  setPage(1);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-header d-flex align-items-center gap-4 flex-wrap">
          <div>
            <h5 className="card-title mb-0">
              <i className="fa fa-list me-2 text-primary" />
              Store Transactions
            </h5>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            {!isAdmin && (
              <span className="ms-auto small text-muted">
                <i className="fa fa-info-circle me-1" />
                To edit an entry, submit an edit request — admin will approve.
              </span>
            )}
            {isSuperAdmin && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setBulkOpen(true)}
              >
                <i className="fa fa-upload me-1" /> Bulk Upload
              </button>
            )}
            <ExcelDownloadButton
              endpoint="/api/stock/store/transactions/export"
              params={{ skuId: skuFilter, entryType: typeFilter }}
              filename="store-transactions.xlsx"
              title="Store Transactions"
              label="Export Excel"
              className="ms-auto"
            />
            {!loading && (
              <span className="badge badge-primary ms-1">
                {totalCount}
              </span>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="display dataTable no-footer w-100">
              <thead>
                <tr>
                  {/* <th>#</th> */}
                  <th>Date</th>
                  <th>Item</th>
                  <th>Type</th>
                  <th>Vendor / Contractor</th>
                  <th>Bill No / V. No</th>
                  <th>Open Qty</th>
                  <th>Receipts Qty</th>
                  <th>Issued Qty</th>
                  <th>Balance Qty</th>
                  <th>Truck No</th>
                  <th>Location / Remarks</th>
                  <th>Issued By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={13} className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2" />
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && transactions.length === 0 && (
                  <tr>
                    <td colSpan={13} className="text-center py-4 text-muted">
                      No transactions found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  transactions.map((t, idx) => {
                    const issuedTo =
                      t.entryType === "issued"
                        ? t.recipientName || "—"
                        : t.supplierId?.name || "—";
                    const pendingReq = pendingByStock[t._id?.toString()];

                    return (
                      <tr key={t._id}>
                        {/* <td className="text-muted small">{idx + 1}</td> */}
                        <td className="small">{fmtDate(t.created)}</td>
                        <td className="fw-normal small">
                          {t.skuId?.skuName || "—"}
                          {t.skuId?.measurement && (
                            <span className="text-muted ms-1">
                              ({t.skuId.measurement})
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${t.entryType === "add" ? "badge-success" : "badge-warning"}`}
                          >
                            {t.entryType === "add" ? "Received" : "Issued"}
                          </span>
                          {t.isUsedInVehicle && (
                            <span
                              className="badge badge-info ms-1"
                              title={`Vehicle: ${t.vehicleId?.vehicleNumber || ""}`}
                            >
                              <i className="fa fa-car" />
                            </span>
                          )}
                        </td>
                        <td className="small">{issuedTo}</td>
                        <td className="small text-muted">
                          {t.invoiceNo || "—"}
                        </td>
                        <td className="small text-muted">
                          {fmtNum(t.prevBalance)}
                        </td>
                        <td className="small fw-semibold text-success">
                          {t.entryType === "add" ? fmtNum(t.qty) : ""}
                        </td>
                        <td className="small fw-semibold text-danger">
                          {t.entryType === "add" ? "" : fmtNum(t.qty)}
                        </td>
                        <td className="small fw-semibold">
                          {fmtNum(t.balance)}
                        </td>
                        <td className="small text-muted">
                          {t.truckNo || "—"}
                        </td>
                        <td className="small text-muted">
                          {[t.location, t.remarks].filter(Boolean).join(" · ") || "—"}
                        </td>
                        <td className="small text-muted">
                          {t.userId?.name || "—"}
                        </td>
                        <td>
                          <div className="d-flex gap-1 align-items-center flex-wrap">
                            {isAdmin ? (
                              /* Super-admin: direct edit + show pending request hint */
                              <div>
                                <button
                                  className={`btn btn-xs ${pendingReq ? "btn-warning" : "btn-primary"}`}
                                  onClick={() => openEdit(t)}
                                  title={
                                    pendingReq
                                      ? `Edit requested by ${pendingReq.requestedBy?.name}: qty ${pendingReq.newQty}`
                                      : "Edit entry directly"
                                  }
                                >
                                  <i className="fa fa-pencil me-1" />
                                  Edit
                                </button>
                                {pendingReq && (
                                  <div
                                    style={{
                                      fontSize: "0.65rem",
                                      color: "#e67e22",
                                      marginTop: 3,
                                      lineHeight: 1.3,
                                      maxWidth: 110,
                                    }}
                                  >
                                    <i className="fa fa-bell me-1" />
                                    {pendingReq.requestedBy?.name} requested
                                    qty&nbsp;→&nbsp;
                                    <strong>{pendingReq.newQty}</strong>
                                  </div>
                                )}
                              </div>
                            ) : pendingReq ? (
                              /* Employee: pending request exists */
                              <span
                                className="badge badge-warning small"
                                title="Edit request pending admin review"
                              >
                                <i className="fa fa-clock-o me-1" />
                                Pending
                              </span>
                            ) : (
                              /* Employee: request edit */
                              <button
                                className="btn btn-xs btn-outline-warning"
                                onClick={() => openRequest(t)}
                                title="Request an edit — admin must approve"
                              >
                                <i className="fa fa-edit me-1" />
                                Request Edit
                              </button>
                            )}
                            <button
                              className="btn btn-xs btn-outline-secondary"
                              onClick={() =>
                                navigate(
                                  `/inventory/sku/${t.skuId?._id}/ledger`,
                                )
                              }
                              title="View SKU ledger"
                            >
                              <i className="fa fa-eye" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="d-flex align-items-center justify-content-between p-3">
              <span className="small text-muted">
                Page {page} of {totalPages} ({totalCount} entries)
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Super-admin direct edit modal ──────────────────────────────────── */}
      {editRow && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Edit Entry
                  {editRow._pendingReq && (
                    <span
                      className="badge badge-warning ms-2"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Edit Requested
                    </span>
                  )}
                </h5>
                <button className="btn-close" onClick={closeEdit} />
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  {editRow._pendingReq && (
                    <div className="alert alert-warning py-2 small mb-3">
                      <i className="fa fa-bell me-1" />
                      <strong>
                        {editRow._pendingReq.requestedBy?.name}
                      </strong>{" "}
                      requested to change qty from{" "}
                      <strong>{editRow.qty}</strong> →{" "}
                      <strong>{editRow._pendingReq.newQty}</strong>.
                      {editRow._pendingReq.reason && (
                        <div className="mt-1 text-muted fst-italic">
                          "{editRow._pendingReq.reason}"
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mb-2 small text-muted">
                    <strong>Item:</strong> {editRow.skuId?.skuName}{" "}
                    &nbsp;|&nbsp;
                    <strong>Date:</strong> {fmtDate(editRow.created)}{" "}
                    &nbsp;|&nbsp;
                    <strong>Type:</strong>{" "}
                    <span
                      className={`badge ${editRow.entryType === "add" ? "badge-success" : "badge-warning"}`}
                    >
                      {editRow.entryType === "add" ? "Received" : "Issued"}
                    </span>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Quantity</label>
                    <input
                      type="number"
                      name="qty"
                      className="form-control"
                      value={editForm.qty}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, qty: e.target.value }))
                      }
                      min={1}
                      required
                    />
                  </div>
                  {editRow.entryType === "add" && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Total Price
                      </label>
                      <input
                        type="number"
                        name="price"
                        className="form-control"
                        value={editForm.price}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, price: e.target.value }))
                        }
                        min={0}
                      />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={editLoading}
                  >
                    {editLoading && (
                      <span className="spinner-border spinner-border-sm me-1" />
                    )}
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Employee request-edit modal ─────────────────────────────────────── */}
      {requestRow && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-edit me-2 text-warning" />
                  Request Edit
                </h5>
                <button className="btn-close" onClick={closeRequest} />
              </div>
              <form onSubmit={handleRequestSubmit}>
                <div className="modal-body">
                  <div className="alert alert-info py-2 small mb-3">
                    This request will be sent to admin for approval. The entry
                    will not change until approved.
                  </div>
                  <div className="mb-2 small text-muted">
                    <strong>Item:</strong> {requestRow.skuId?.skuName}{" "}
                    &nbsp;|&nbsp;
                    <strong>Current Qty:</strong> {requestRow.qty} &nbsp;|&nbsp;
                    <strong>Date:</strong> {fmtDate(requestRow.created)}
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      New Quantity <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={requestForm.newQty}
                      onChange={(e) =>
                        setRequestForm((f) => ({
                          ...f,
                          newQty: e.target.value,
                        }))
                      }
                      min={1}
                      required
                    />
                  </div>
                  {requestRow.entryType === "add" && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        New Total Price
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        value={requestForm.newPrice}
                        onChange={(e) =>
                          setRequestForm((f) => ({
                            ...f,
                            newPrice: e.target.value,
                          }))
                        }
                        min={0}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={requestForm.reason}
                      onChange={(e) =>
                        setRequestForm((f) => ({
                          ...f,
                          reason: e.target.value,
                        }))
                      }
                      placeholder="Explain why this entry needs to be changed…"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeRequest}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-warning"
                    disabled={requestLoading}
                  >
                    {requestLoading && (
                      <span className="spinner-border spinner-border-sm me-1" />
                    )}
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default List;
